import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';

import { defaultImage } from 'src/constants/constants';
import { ComponentBuildingFloorplan } from 'apolloClient/types/Building';
import { SEARCH_FLOORPLANS_BY_LISTING_ID } from 'apolloClient/queries/searchListings';
import { Building, EntityWithId, StrapiMediaInfo } from 'apolloClient/types';
import { Listing } from 'apolloClient/types/Units';
import { flattenStrapiDataItem } from 'lib/flattenStrapiBulkDataItems';

interface UseFloorplanImageParams {
  initialSitePlan: boolean;
  initialActiveRowIndex: number | null;
  entity: Building | Listing;
  bodyType: 'listing' | 'building';
  buildingFloorPlanImage?: Building['floorPlanImage'];
}

export function useFloorplanImage({
  initialSitePlan,
  initialActiveRowIndex,
  entity,
  bodyType,
  buildingFloorPlanImage,
}: UseFloorplanImageParams) {
  const listingId = (entity as EntityWithId<Listing>).id;

  const [sitePlan, setSitePlan] = useState(initialSitePlan);
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(
    initialActiveRowIndex
  );

  useEffect(() => {
    setSitePlan(initialSitePlan);
    setActiveRowIndex(initialActiveRowIndex);
  }, [bodyType, listingId, initialSitePlan, initialActiveRowIndex]);

  const [fetchFloorplan, { data }] = useLazyQuery<{
    getFloorplanByListingId: ComponentBuildingFloorplan;
  }>(SEARCH_FLOORPLANS_BY_LISTING_ID);

  const floorplan = data?.getFloorplanByListingId;

  useEffect(() => {
    if (bodyType !== 'listing') return;

    fetchFloorplan({
      variables: {
        id: Number(listingId),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyType, listingId]);

  const openSitePlan = () => {
    setSitePlan(true);
    setActiveRowIndex(null);
  };
  const openImage = (tab: number): void => {
    setSitePlan(false);
    setActiveRowIndex(tab);
  };

  function getFloorplanImage(): StrapiMediaInfo | undefined {
    if (bodyType === 'building') {
      const { floorplans, floorPlanImage } = entity as Building;
      const floorplanSitePlanImageUrl = floorPlanImage?.data?.attributes;

      if (sitePlan) {
        return floorplanSitePlanImageUrl;
      }

      const currentFloorplan =
        floorplans?.[activeRowIndex === null ? -1 : activeRowIndex];

      return currentFloorplan?.image?.data?.attributes;
    }

    if (bodyType === 'listing') {
      if (sitePlan) {
        return flattenStrapiDataItem(buildingFloorPlanImage?.data);
      }

      const image = floorplan?.image?.data;
      return flattenStrapiDataItem(image);
    }
  }

  const image = getFloorplanImage();
  const imageSrc = image?.url || defaultImage;

  return {
    sitePlan,
    activeRowIndex,
    floorplan,
    openSitePlan,
    openImage,
    imageSrc,
  };
}
